import React, { useEffect } from 'react';
import { LegalDocumentLayout } from './common/LegalDocumentLayout';
import { LegalSection } from './common/LegalSection';

/**
 * Warunki rezerwacji i zakupu — dokument transakcyjny akceptowany przy
 * zakupie/rezerwacji (docelowo linkowany z pluginu Commerce). Świadomie
 * ukryty: nie ma go w nawigacji ani w stopce landingu, a poniższy efekt
 * ustawia `robots: noindex`, żeby nie wchodził do wyszukiwarek. Meta jest
 * usuwane przy odmontowaniu, żeby nie „zaraziło" pozostałych tras SPA.
 *
 * Treść pochodzi z jednego źródła (vault CRM: Warunki-rezerwacji-i-anulowania).
 * Iris osadza treść bez zmian — redakcja należy do Klio.
 */
export const TermsBooking: React.FC = () => {
  useEffect(() => {
    document.title = 'Warunki rezerwacji i zakupu - Spotto';

    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(robots);

    return () => {
      document.head.removeChild(robots);
      document.title = 'Spotto - Sport Facility Scheduler';
    };
  }, []);

  return (
    <LegalDocumentLayout title={'Warunki rezerwacji\ni zakupu'}>
      {/* Wersja + data wejścia w życie */}
      <div className="text-center -mt-2">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink-on-dark-muted">
          Data wejścia w życie: 28 stycznia 2026
        </p>
        <p className="mt-1 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-ink-on-dark-muted/70">
          Wersja dokumentu: 2 września 2026
        </p>
      </div>

      {/* Preambuła */}
      <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] px-6 py-5 text-sm text-ink-on-dark/85 leading-relaxed">
        <p>
          Niniejsze warunki stanowią ogólne zasady rezerwacji i sprzedaży obowiązujące na
          platformie SPOTTO. Poszczególne parametry — w szczególności termin i zasady anulowania
          oraz sposób dokumentowania sprzedaży — mogą zostać dostosowane przez danego Partnera
          Biznesowego. W zakresie, w jakim Partner Biznesowy określił własne zasady (pkt 6), mają
          one pierwszeństwo przed niniejszymi postanowieniami ogólnymi, o ile są zgodne
          z bezwzględnie obowiązującymi przepisami prawa, w tym z prawami konsumenta.
        </p>
      </div>

      <LegalSection number={1} title="Płatność za rezerwację">
        <p>
          Wszystkie rezerwacje dokonywane za pośrednictwem platformy SPOTTO są płatne online
          w momencie ich składania. Płatność realizowana jest za pośrednictwem operatora płatności
          TPAY. Środki, po potrąceniu kosztów operacyjnych (TPAY i SPOTTO), są przesyłane na konto
          Partnera Biznesowego.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Dokumentowanie zakupu (faktura / paragon / potwierdzenie)">
        <p>
          Sprzedawcą usługi lub produktu jest <strong className="font-medium text-ink-on-dark">Partner
          Biznesowy</strong> i to on dokumentuje sprzedaż zgodnie z obowiązującymi przepisami
          podatkowymi. Platforma SPOTTO pośredniczy wyłącznie w procesie rezerwacji i płatności.
        </p>
        <p>
          Bezpośrednio po opłaceniu rezerwacji Użytkownik otrzymuje na adres e-mail wskazany
          w profilu <strong className="font-medium text-ink-on-dark">potwierdzenie zakupu</strong>{' '}
          zawierające przedmiot rezerwacji, kwotę oraz dane transakcji.
        </p>
        <p>
          W zależności od zasad rozliczeń danego Partnera Biznesowego dokumentem sprzedaży może być
          faktura albo paragon. Akceptując niniejsze warunki i dokonując zakupu, Użytkownik wyraża
          zgodę na wystawianie i przesyłanie dokumentów sprzedaży (w tym faktury lub paragonu){' '}
          <strong className="font-medium text-ink-on-dark">w postaci elektronicznej</strong> na
          wskazany adres e-mail, zamiast w postaci papierowej. Na życzenie Użytkownika dokument
          może zostać udostępniony również w postaci papierowej w obiekcie Partnera Biznesowego.
        </p>
        <p>
          Niektórzy Partnerzy Biznesowi wystawiają fakturę automatycznie do każdego zakupu. Jeżeli
          Użytkownik chce otrzymać fakturę wystawioną na dane działalności gospodarczej, zobowiązany
          jest podać numer NIP przed dokonaniem zakupu; w przeciwnym razie dokument zostaje
          wystawiony na dane konsumenta.
        </p>
        <p>
          Niezależnie od powyższego, Użytkownik będący konsumentem ma prawo{' '}
          <strong className="font-medium text-ink-on-dark">zażądać wystawienia faktury</strong>.
          Żądanie można zgłosić przy zakupie albo później, w terminie wynikającym z przepisów prawa.
          Fakturę wystawia Partner Biznesowy i przesyła ją na wskazany adres e-mail.
        </p>
      </LegalSection>

      <LegalSection number={3} title="Odstąpienie od umowy">
        <p>
          Zgodnie z Art. 38, punktem 12 Praw Konsumenta „Wyjątki od prawa do odstąpienia od umowy”,
          dla umów zawartych na odległość (w tym online) oraz poza lokalem przedsiębiorstwa, nie ma
          możliwości odstąpienia od rezerwacji obiektów sportowych i usług powiązanych, z wyznaczonym
          terminem lub okresem świadczenia usługi.
        </p>
        <p>
          Akceptując niniejsze zapisy, poświadczam zapoznanie się z obowiązującymi regulacjami
          i ograniczeniami.
        </p>
      </LegalSection>

      <LegalSection number={4} title="Modyfikacja i anulowanie terminu rezerwacji">
        <p>
          Poniższe warunki obowiązują domyślnie, o ile Partner Biznesowy nie określił dla swojego
          obiektu zasad odmiennych (pkt 6).
        </p>
        <p>
          Wdrożony model rezerwacji dopuszcza możliwość modyfikacji lub anulowania rezerwacji
          w terminie do 48 godzin przed planowanym zdarzeniem.
        </p>
        <p>
          Modyfikacja rezerwacji jest możliwa poprzez kontakt telefoniczny z Partnerem Biznesowym
          (obiektem), w terminie nie krótszym niż 48 godzin przed zdarzeniem. Modyfikacja jest
          dokonywana przez Partnera Biznesowego, a dokonana zmiana będzie automatycznie widoczna
          w rezerwacjach aplikacji SPOTTO. Modyfikacja jest wykonywana bezkosztowo.
        </p>
        <p>
          Anulowanie jest możliwe do 48 godzin przed terminem rezerwacji. W przypadku skutecznego
          anulowania rezerwujący otrzymuje zwrot w wysokości 90% wartości rezerwacji. Pozostałe 10%
          wartości stanowią koszty operacyjne, które nie podlegają zwrotowi. Zwrot środków jest
          dokonywany bezpośrednio przez Partnera Biznesowego w formie ustalonej pomiędzy klientem
          a Partnerem Biznesowym.
        </p>
        <p>
          Anulowanie i modyfikacje w czasie krótszym niż 48 godzin do zaplanowanego wydarzenia są
          niemożliwe, a koszty nie podlegają zwrotowi.
        </p>
        <p>Koszty operacyjne nie podlegają zwrotowi w żadnym przypadku anulacji.</p>
        <p>
          Dane kontaktowe Partnera Biznesowego znajdują się na jego stronie, która wraz
          z regulaminem jest dostępna w widoku obiektu przy rezerwacji.
        </p>
      </LegalSection>

      <LegalSection number={5} title="Niestawienie się">
        <p>
          W przypadku niestawienia się na zarezerwowany termin bez wcześniejszej anulacji,
          Użytkownikowi nie przysługuje zwrot środków.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Wyjątki i ustalenia dodatkowe">
        <p>
          Regulaminy, warunki rezerwacji i anulacji oraz inne regulacje portalu SPOTTO nie
          zastępują szczegółowych regulaminów obiektów lub zasad ustalonych dla danego obiektu.
          W szczególności Partner Biznesowy może określić własne zasady dotyczące poszczególnych
          przypadków, bardziej lub mniej liberalne w stosunku do obecnych postanowień. Szczegółowy
          regulamin obiektu znajduje się na stronie obiektu, która dostępna jest w profilu danego
          Partnera Biznesowego na platformie SPOTTO.
        </p>
        <p>
          Odpowiedzialność platformy SPOTTO ogranicza się do zapewnienia ciągłości działania
          funkcjonalności systemu rezerwacji i sprzedaży produktów i usług. Wszelkie roszczenia
          wynikające z bezpośrednich, specyficznych ustaleń pomiędzy Użytkownikiem a Partnerem
          Biznesowym muszą być rozwiązywane bezpośrednio pomiędzy zaangażowanymi stronami.
        </p>
      </LegalSection>

      <LegalSection number={7} title="Zwrot środków">
        <p>
          Zwrot środków, jeśli przysługuje, realizowany jest bezpośrednio pomiędzy Partnerem
          Biznesowym a Użytkownikiem. Transakcja powinna zostać rozliczona w terminie do 14 dni
          roboczych od momentu anulacji, na rachunek wskazany przez Użytkownika, o ile strony nie
          ustalą inaczej.
        </p>
      </LegalSection>

      <LegalSection number={8} title="Reklamacje">
        <p>
          Reklamacje dotyczące zwrotów należy kierować bezpośrednio do Partnera Biznesowego.
          W przypadku trudności z kontaktem możliwy jest kontakt z działem Reklamacji SPOTTO.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Definicje">
        <p>
          <strong className="font-medium text-ink-on-dark">Użytkownik</strong> — użytkownik
          platformy SPOTTO, który dokonuje rezerwacji czasu obiektu lub zakupów produktów i usług
          za pomocą platformy SPOTTO.
        </p>
        <p>
          <strong className="font-medium text-ink-on-dark">Platforma SPOTTO</strong> — platforma
          rezerwacyjno-sprzedażowa, aplikacja mobilna umożliwiająca rezerwację obiektów oraz zakupy
          produktów i usług od Partnerów Biznesowych.
        </p>
        <p>
          <strong className="font-medium text-ink-on-dark">Partner Biznesowy</strong> — podmiot
          gospodarczy, który jest właścicielem obiektu i oferuje produkty i usługi powiązane z danym
          obiektem, z którym Użytkownik dokonuje transakcji za pośrednictwem platformy SPOTTO. Pełne
          dane kontaktowe Partnera Biznesowego są dostępne na stronie internetowej Partnera, która
          jest dostępna przez link „Regulamin Obiektu”.
        </p>
        <p>
          <strong className="font-medium text-ink-on-dark">Koszty operacyjne</strong> — koszty
          związane z obsługą płatności elektronicznych, koszty administracyjne oraz koszty obsługi
          systemu rezerwacyjnego.
        </p>
      </LegalSection>
    </LegalDocumentLayout>
  );
};
